// View-as helpers and pure rules for owners and administrators (docs/api/VIEW-AS.md).
// Owners and admins can open a time-boxed (60-minute) read-only view of Sidetone as another
// active member of the current workspace sees it; only an owner may view as another owner.
import type { Session, User, ViewAs } from '@bcio-voice/contracts';

/** Role IDs of the viewer in the active workspace. */
export function viewerTenantRoles(session: Pick<Session, 'tenant' | 'memberships'>): string[] {
  const tenantId = session.tenant?.id;
  if (!tenantId) return [];
  return session.memberships.find(m => m.tenant_id === tenantId)?.role_ids ?? [];
}

/** Whether the signed-in principal is an owner or admin who may start a View-as session. */
export function canStartViewAs(session: Pick<Session, 'user' | 'tenant' | 'memberships' | 'view_as'>): boolean {
  if (!session.user || !session.tenant || session.view_as) return false;
  const roles = viewerTenantRoles(session);
  return roles.includes('owner') || roles.includes('admin');
}

/** Whether the viewer may open Sidetone as `target`. Viewed user cannot be another owner unless viewer is an owner. */
export function canViewAsUser(
  session: Pick<Session, 'user' | 'tenant' | 'memberships' | 'view_as'>,
  target: Pick<User, 'id' | 'active' | 'role_ids'>,
): boolean {
  if (!canStartViewAs(session)) return false;
  if (!target.active || target.id === session.user?.id) return false;
  const viewerRoles = viewerTenantRoles(session);
  if (target.role_ids.includes('owner') && !viewerRoles.includes('owner')) return false;
  return true;
}

/** Active workspace members the viewer can choose in the View-as picker. */
export function viewAsCandidates(
  session: Pick<Session, 'user' | 'tenant' | 'memberships' | 'view_as'>,
  roster: readonly User[] | null | undefined,
): User[] {
  if (!roster || !canStartViewAs(session)) return [];
  return roster.filter(u => canViewAsUser(session, u));
}

/** Formats remaining time until `expiresAt` as MM:SS (e.g. "59:42"). */
export function formatViewAsCountdown(expiresAt: string | null | undefined, nowMs = Date.now()): string {
  if (!expiresAt) return '';
  const targetMs = Date.parse(expiresAt);
  if (!Number.isFinite(targetMs)) return '';
  const totalSeconds = Math.max(0, Math.ceil((targetMs - nowMs) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Phone dock placeholder copy when viewing as another user. */
export function viewAsPhoneDockCopy(viewAs: Pick<ViewAs, 'display_name'>): string {
  return `Viewing as ${viewAs.display_name}: phone unavailable in this mode`;
}
